import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { TASK_SCHEDULER_TYPES } from "./taskSchedulerConstants";

function typeLabel(value) {
    const row = TASK_SCHEDULER_TYPES.find((t) => t.value === value);
    return row?.label ?? value ?? "";
}

function formatEpoch(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return moment(n).format("YYYY-MM-DD HH:mm");
}

const wrapCellSx = {
    display: "flex",
    alignItems: "center",
    height: "100%",
    width: "100%",
    py: 0.75,
};

const wrapTextSx = {
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    wordBreak: "break-word",
    lineHeight: 1.4,
    width: "100%",
};

function WrapCellText({ children, color }) {
    return (
        <Box sx={wrapCellSx}>
            <Typography variant="body2" color={color} sx={wrapTextSx}>
                {children}
            </Typography>
        </Box>
    );
}

export function getColumns({ onToggleEnabled, onDuplicate, onRemove, duplicatingId, removingId }) {
    return [
        {
            field: "enabled",
            headerName: "Enabled",
            flex: 0.45,
            minWidth: 90,
            renderCell: (params) => (
                <Box
                    sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Switch
                        checked={Boolean(params.value)}
                        size="small"
                        onChange={(e) => onToggleEnabled(params.row, e.target.checked)}
                    />
                </Box>
            ),
        },
        {
            field: "cron",
            headerName: "Cron",
            flex: 1,
            minWidth: 180,
            renderCell: (params) => <WrapCellText>{params.value || ""}</WrapCellText>,
        },
        {
            field: "type",
            headerName: "Type",
            flex: 1,
            minWidth: 180,
            valueGetter: (value) => typeLabel(value),
            renderCell: (params) => <WrapCellText>{params.value || ""}</WrapCellText>,
        },
        {
            field: "memo",
            headerName: "Memo",
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params) => {
                const text = params.value == null || params.value === "" ? "" : String(params.value);
                if (!text) {
                    return <WrapCellText color="text.secondary">—</WrapCellText>;
                }
                return <WrapCellText>{text}</WrapCellText>;
            },
        },
        {
            field: "latestRunAt",
            headerName: "Latest run",
            width: 160,
            valueFormatter: (value) => formatEpoch(value) || "—",
        },
        {
            field: "createdAt",
            headerName: "Created",
            width: 160,
            valueFormatter: (value) => formatEpoch(value),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Box
                    sx={{ display: "flex", alignItems: "center", height: "100%", gap: 0.25 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip title="Duplicate">
                        <span>
                            <IconButton
                                size="small"
                                aria-label="Duplicate schedule"
                                disabled={duplicatingId === params.row.id || removingId === params.row.id}
                                onClick={() => onDuplicate?.(params.row)}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Remove">
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                aria-label="Remove schedule"
                                disabled={duplicatingId === params.row.id || removingId === params.row.id}
                                onClick={() => onRemove?.(params.row)}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ),
        },
    ];
}
